import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import { Field, reduxForm } from 'redux-form';
import IconButton from '@material-ui/core/IconButton';
import SearchIcon from '@material-ui/icons/Search';
import MenuItem from '@material-ui/core/MenuItem';

import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import ORTypography from 'components/ORTypography/ORTypography';
import { required } from 'services/validationMsgs';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  searchOrgs: PropTypes.func.isRequired,
  sortOrgs: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    orgSearchList: PropTypes.shape({
      list: PropTypes.arrayOf(PropTypes.shape({})),
      query: PropTypes.string,
    }),
  }).isRequired,
  handleChangePage: PropTypes.func.isRequired,
};

export function UnstyledOrgImpersonationForm({
  classes,
  handleSubmit,
  searchOrgs,
  sortOrgs,
  valid,
  user,
  handleChangePage,
}) {
  const { list, query } = user.orgSearchList;
  const submitHandler = (data) => {
    searchOrgs(data.search);
    handleChangePage(null, 0);
  };

  const wrappedIcon = (
    <IconButton
      type="submit"
      disabled={!valid}
      variant="contained"
      className={classes.searchButton}
    >
      <SearchIcon color="secondary" className={classes.searchIcon} />
    </IconButton>
  );
  const adornmentObject = (value, position) => ({
    value,
    position,
  });

  const sortValues = [
    {
      value: 'companyNameAsc',
      label: 'Organization Name, A - Z',
    },
    {
      value: 'companyNameDesc',
      label: 'Organization Name, Z - A',
    },
    {
      value: 'dtCreatedDesc',
      label: 'Date Created, Newest',
    },
    {
      value: 'dtCreatedAsc',
      label: 'Date Created, Oldest',
    },
  ];

  const sortOptions = sortValues.map((option, index) => (
    <MenuItem key={index.toString()} value={option.value}>
      {option.label}
    </MenuItem>
  ));

  const searchResults =
    list.length || query ? (
      <ORTypography className={classes.searchResultsText}>
        {`${list.length || 'No'}\u00A0Search Results for:`}
        &nbsp;
        <span className="searchResultsTextLength">{query}</span>
      </ORTypography>
    ) : (
      <ORTypography color="secondary" className={classes.searchResultsText}>
        Search for an Organization by Name, Email, or Id
      </ORTypography>
    );

  return (
    <div className={classes.formContainer}>
      <form className={classes.container} onSubmit={handleSubmit(submitHandler)}>
        <Field
          fullWidth
          name="search"
          adornment={adornmentObject(wrappedIcon, 'end')}
          component={WrappedTextField}
          placeholder="Search By Name, Email, or ID"
          validate={[required]}
          className={classes.searchField}
        />
        <Field
          fullWidth
          select
          name="sort"
          disabled={!list.length}
          adornment={adornmentObject('Sort\u00A0By:', 'start')}
          component={WrappedTextField}
          className={classes.searchField}
          onChange={(event) => sortOrgs(event.target.value)}
        >
          {sortOptions}
        </Field>
      </form>
      <div className={classes.searchResultsTextContainer}>{searchResults}</div>
    </div>
  );
}

const styles = (theme) => ({
  container: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',

    [theme.breakpoints.down('md')]: {
      flexFlow: 'column nowrap',
    },
  },
  searchField: {
    padding: '8px',
  },
  searchButton: {
    left: theme.spacing(1.5),
    color: theme.palette.secondary,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0)',
    },
  },
  searchIcon: {
    color: theme.palette.secondary,
  },
  formContainer: {
    display: 'flex',
    flexFlow: 'column',
  },
  searchResultsText: {
    '& .searchResultsTextLength': {
      color: theme.palette.secondary.main,
    },
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexFlow: 'column nowrap',
      alignItems: 'center',
    },
  },
  searchResultsTextContainer: {
    display: 'flex',
    padding: '8px',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'center',
    },
  },
});

UnstyledOrgImpersonationForm.propTypes = propTypes;

export default withStyles(styles)(
  reduxForm({ form: 'orgImpersonation' })(UnstyledOrgImpersonationForm),
);
